const API_URL = process.env.NODE_ENV === 'production' 
  ? 'https://creative-footprint-api-458365176018.us-west1.run.app/api'
  : 'http://localhost:5001/api';

// Get stored auth token
export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Store auth token
export const setAuthToken = (token) => {
  localStorage.setItem('authToken', token);
};

// Remove auth token
export const removeAuthToken = () => {
  localStorage.removeItem('authToken');
};

// Get current user from localStorage
export const getCurrentUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

// Store user data
export const setCurrentUser = (user) => {
  localStorage.setItem('user', JSON.stringify(user));
};

// Remove user data
export const removeCurrentUser = () => {
  localStorage.removeItem('user');
};

// Helper function to make authenticated requests
const makeRequest = async (url, method = 'GET', body = null) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    method,
    headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`📡 API Call: ${method} ${url}`);
    if (body && body.chartImageBase64) {
      console.log(`   Data size: Chart=${body.chartImageBase64.length}, PDF=${body.pdfBase64.length}`);
    }
    
    const response = await fetch(url, config);
    console.log(`   Response status: ${response.status}`);
    
    // Check content-type to determine how to parse response
    const contentType = response.headers.get('content-type');
    let data;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      // If not JSON, get text response
      const text = await response.text();
      console.error(`   ⚠️  Non-JSON response (${contentType}): ${text.substring(0, 200)}`);
      throw new Error(`Server returned non-JSON response (${response.status}): ${text.substring(0, 100)}`);
    }

    if (!response.ok) {
      console.error(`   ❌ Error: ${data.error || 'API request failed'}`);
      throw new Error(data.error || 'API request failed');
    }

    console.log(`   ✅ Success`);
    return data;
  } catch (error) {
    console.error(`   ❌ Request failed: ${error.message}`);
    throw error;
  }
};

// Authentication APIs

export const signup = async (email, password, firstName = '', lastName = '') => {
  const response = await makeRequest(`${API_URL}/auth/signup`, 'POST', {
    email,
    password,
    firstName,
    lastName,
  });

  setAuthToken(response.token);
  setCurrentUser(response.user);
  return response;
};

export const login = async (email, password) => {
  const response = await makeRequest(`${API_URL}/auth/login`, 'POST', {
    email,
    password,
  });

  setAuthToken(response.token);
  setCurrentUser(response.user);
  return response;
};

export const socialLogin = async (provider, code, redirectUri) => {
  const response = await makeRequest(`${API_URL}/auth/social`, 'POST', {
    provider,
    code,
    redirectUri,
  });

  setAuthToken(response.token);
  setCurrentUser(response.user);
  return response;
};

export const logout = () => {
  removeAuthToken();
  removeCurrentUser();
};

export const getProfile = async () => {
  return makeRequest(`${API_URL}/auth/profile`, 'GET');
};

// Survey APIs

export const submitSurvey = async (responses, scores) => {
  return makeRequest(`${API_URL}/survey/submit`, 'POST', {
    responses,
    scores,
  });
};

export const getResults = async () => {
  return makeRequest(`${API_URL}/survey/results`, 'GET');
};

export const getSurveyResponses = async () => {
  return makeRequest(`${API_URL}/survey/responses`, 'GET');
};

export const shareResults = async (recipientEmail, chartImageBase64, pdfBase64, profileName) => {
  return makeRequest(`${API_URL}/survey/share`, 'POST', {
    recipientEmail,
    chartImageBase64,
    pdfBase64,
    profileName
  });
};

// Peer Assessment APIs

export const invitePeers = async (emails) => {
  return makeRequest(`${API_URL}/survey/invite-peers`, 'POST', {
    emails
  });
};

export const getPeerSurvey = async (token) => {
  // Public endpoint - no authentication required
  const response = await fetch(`${API_URL}/survey/peer/${token}`);
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch peer survey');
  }
  
  return data;
};

export const submitPeerAssessment = async (token, responses, scores) => {
  // Public endpoint - no authentication required
  const response = await fetch(`${API_URL}/survey/peer/${token}/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ responses, scores })
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || 'Failed to submit peer assessment');
  }
  
  return data;
};

export const getComparison = async (userId) => {
  return makeRequest(`${API_URL}/survey/comparison/${userId}`, 'GET');
};

export const sendReminders = async () => {
  return makeRequest(`${API_URL}/survey/send-reminders`, 'POST');
};

export const shareResultsViaEmail = async (shareData) => {
  return makeRequest(`${API_URL}/survey/share-results`, 'POST', shareData);
};
export const getDashboardData = async () => {
  return makeRequest(`${API_URL}/survey/dashboard/data`, 'GET');
};